/* OnSumn — Flows Event Feed (T10): REACHABLE TONIGHT FROM HOME.
 * A standalone discovery list + the accessible surface for the map (map
 * markers are aria-hidden; every marker's event exists here as a row, §6.3).
 * Minute buckets (§4.4), minutes-led rows (§4.3), last-train chips (§5.3),
 * mobile inline route expansion (§5.4), no-home city-grouped variant (§4.6). */

const FEED_LENS = {
  dark:  { page: '#0C0F0F', panel: '#1A1C1C', ink: '#E9E7E2', muted: '#94897B', hairline: 'rgba(255,255,255,0.08)', reach1: '#FF6E32' },
  light: { page: '#F5F2EC', panel: '#FBF9F4', ink: '#191512', muted: '#6B5B4F', hairline: 'rgba(26,23,20,0.12)', reach1: '#BF3B00' },
};
const FEED_RAMP = {
  dark:  { reach1: '#FF6E32', reach2: '#FFB778', reach3: '#8FBF9F', reach4: '#A3BDCC', dim: '#8A8578' },
  light: { reach1: '#BF3B00', reach2: '#8E4E00', reach3: '#395542', reach4: '#3F5E70', dim: '#6B5B4F' },
};
// Canonical category palette (central-palette-lockstep) — window is authoritative.
const FEED_CAT_COLORS = (typeof window !== 'undefined' && window.CATEGORY_COLORS) || {
  'MUSIC': '#FF4F00', 'CARDS': '#7A99AC', 'ART': '#5A7A68',
  'FOOD': '#D57800', 'NIGHTLIFE': '#9F7BE5', 'COMMUNITY': '#395542',
};

// Copy — spec §7 (feed strings), verbatim.
const FEED_COPY = {
  F1_TITLE: 'REACHABLE TONIGHT',
  F2_TITLE: 'TONIGHT, ACROSS THE BAY',
  F10: 'THE ONE YOU CAME FOR',   // §4 pinned deep-link section (Phase 2)
  F6: 'Typical times, not gospel — check before you ride.',
  E1_A: 'QUIET WITHIN THE HOUR OF ',
  E1_B: "The Bay's still moving — check the long haul or browse everything.",
  E2: 'NOTHING ON THE RAILS TONIGHT. Rare. Check back tomorrow.',
  E3: "TRAVEL TIMES ARE DOWN — HERE'S TONIGHT ANYWAY.",
  SETHOME_LINE: 'Your nearest rail stop becomes the lens — every show tonight gets a time from home.',
  FIND: 'FIND MY STATION',
  PICK: 'PICK IT MYSELF',
  JUMP: 'JUMP TO MAP',            // mobile home-set in-page jump (2026-07-21)
};

// No-home hub grouping (§4.6) — nearest CITY_HUBS hub → one of the 4 F5 groups.
const HUB_GROUP = {
  'bart-powell': 'SF', 'bart-daly-city': 'SF',
  'bart-12th-st': 'OAKLAND',
  'bart-richmond': 'EAST BAY', 'bart-el-cerrito-del-norte': 'EAST BAY',
  'caltrain-diridon': 'SOUTH BAY',
};
const GROUP_ORDER = ['SF', 'OAKLAND', 'EAST BAY', 'SOUTH BAY'];

// Goal 2 — per-bucket feed cap. The three visible REACHABLE TONIGHT buckets
// (under20/quick/worth) render their soonest FEED_BUCKET_CAP rows by default; a
// busy night's overflow tucks behind a per-bucket SHOW MORE (n) reveal. THE LONG
// HAUL is exempt (it has its own collapse). Chrono order + after-last sinking are
// preserved by the upstream sort — the cap only slices the already-sorted head.
const FEED_BUCKET_CAP = 5;

/* ── One feed row (§4.3) — whole row is the tap target ── */
function FlowsFeedRow({
  row, isMobile, isExpanded, onTap, homeStation,
  routePrev, stations, lines, lastTrain, reducedMotion, darkMode, radius,
}) {
  const FF = window.FlowsFeedFormat;
  const [hov, setHov] = React.useState(false);
  const lens = darkMode ? FEED_LENS.dark : FEED_LENS.light;
  const ramp = darkMode ? FEED_RAMP.dark : FEED_RAMP.light;
  const { event, station, reachMin, band, headway, chip } = row;

  const noHome = reachMin == null;
  const afterLast = chip && chip.colorKey === 'afterlast';
  const numColor = afterLast ? ramp.dim : (band ? ramp[band] : lens.ink);
  const figure = (!noHome && FF) ? FF.reachFigureParts(reachMin, headway) : null;
  // Adult Clipper fare for the row's trip (lib/flows-fares.js) — '' until
  // fares.json has loaded or when a leg can't be priced; the number block then
  // ends at the unit, nothing else.
  const fare = (figure && row.fareCents != null && window.FlowsFares) ? window.FlowsFares.formatFare(row.fareCents) : '';

  // station is null in the stations-failed raw fallback (§4.5 E3) — the row
  // renders without its station line.
  const sysInfo = station ? ((window.SYSTEM_INFO && window.SYSTEM_INFO[station.system]) || { color: lens.muted }) : null;
  const stationName = station ? (window.displayStationName ? window.displayStationName(station) : station.name) : null;
  const catColor = FEED_CAT_COLORS[event.category];

  // aria-label (§6.3): no color-only meaning; tier is in the words.
  let aria = `${event.title}, ${event.venue || ''}`;
  if (event.time) aria += `, ${event.time}`;
  if (!noHome && window.formatReachTimeAria) aria += `, ${window.formatReachTimeAria(reachMin, headway)} from home`;
  if (fare) aria += `, ${fare} adult Clipper fare`;
  if (chip && chip.aria) aria += `, ${chip.aria}`;

  const rowBg = hov
    ? (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(26,23,20,0.06)')
    : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(26,23,20,0.03)');

  const chipEl = chip ? <FlowsLastTrainChip info={chip} darkMode={darkMode} /> : null;

  return (
    // Goal 6 audit: the renderRow wrapper (the <ul>'s direct child) is now the
    // real <li>; this inner node was an li nested under a div — ul > div > li —
    // which read as a zero-item list to AT. Row spacing stays here.
    <div style={{ marginBottom: 8 }}>
      <button
        className="focus-double-ring"
        aria-label={aria}
        aria-expanded={isMobile ? isExpanded : undefined}
        onClick={() => onTap(row)}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? 6 : 14, width: '100%', textAlign: 'left', cursor: 'pointer',
          minHeight: 56, padding: '12px 14px',
          background: rowBg, border: `1px solid ${lens.hairline}`,
          borderRadius: Math.min(radius, 12),
          transition: 'background 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 14, width: '100%', minWidth: 0 }}>
          {/* Number block (§4.3) — minutes lead; omitted in the no-home variant */}
          {figure && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minWidth: 52, flexShrink: 0,
            }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontWeight: 900,
                fontSize: figure.isRange ? 16 : 22, color: numColor, lineHeight: 1.05,
                textAlign: 'center', wordBreak: 'keep-all',
              }}>{figure.figure}</span>
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 9, fontWeight: 700,
                letterSpacing: '0.12em', textTransform: 'uppercase', color: lens.muted,
                lineHeight: 1, marginTop: 3,
              }}>{figure.unit || 'MIN'}</span>
              {fare && (
                <span title="Adult Clipper fare (511 SF Bay)" style={{
                  fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.04em', color: lens.muted, lineHeight: 1, marginTop: 4,
                  whiteSpace: 'nowrap',
                }}>{fare}</span>
              )}
            </div>
          )}

          {/* Main block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {catColor && (
                <span aria-hidden="true" style={{
                  width: 8, height: 8, borderRadius: '50%', background: catColor, flexShrink: 0,
                }} />
              )}
              <span style={{
                fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 700, color: lens.ink,
                lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{event.title}</span>
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, color: lens.muted,
              lineHeight: 1.4, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{row.pinned && event.date ? `${event.date} · ` : ''}{event.venue}{event.time ? ` · ${event.time}` : ''}</div>
            {stationName && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4, marginTop: 4,
                fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 500,
                // Goal 6 audit: station line is wayfinding, not decoration — the
                // alpha-faded copies sat ~3.4:1 (dark). Solid lens.muted = AA.
                color: lens.muted,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                <span aria-hidden="true" style={{
                  width: 6, height: 6, borderRadius: '50%', background: sysInfo.color, flexShrink: 0,
                }} />
                {stationName}
              </div>
            )}
          </div>

          {/* Chip — desktop right-aligned */}
          {!isMobile && chipEl && <div style={{ flexShrink: 0 }}>{chipEl}</div>}
        </div>

        {/* Chip — mobile drops to its own line, left-aligned (§4.3) */}
        {isMobile && chipEl && <div style={{ marginTop: 2 }}>{chipEl}</div>}
      </button>

      {/* Mobile inline route expansion (§5.4) */}
      {isMobile && (
        <FlowsExpansion open={isExpanded} reduced={reducedMotion}>
          {isExpanded && window.FlowsRoutePanel && homeStation && !noHome && (
            <div style={{
              padding: '4px 14px 14px',
              border: `1px solid ${lens.hairline}`, borderTop: 'none',
              borderRadius: `0 0 ${Math.min(radius, 12)}px ${Math.min(radius, 12)}px`,
              background: darkMode ? 'rgba(255,255,255,0.015)' : 'rgba(26,23,20,0.015)',
            }}>
              <window.FlowsRoutePanel
                homeId={homeStation.id}
                station={station}
                arrivalLabel={event.venue}
                ctaUrl={`https://www.google.com/maps/dir/?api=1&destination=${event.lat},${event.lng}&travelmode=transit`}
                eventStartMin={row.startMin}
                dayTypeOverride={row.tierDayType}
                todayYmdOverride={row.tierYmd}
                routePrev={routePrev}
                stations={stations}
                lines={lines}
                lastTrain={lastTrain}
                darkMode={darkMode}
                radius={radius}
              />
            </div>
          )}
        </FlowsExpansion>
      )}
    </div>
  );
}

/* Mobile expansion wrapper — grid-rows 0fr→1fr (house pattern from FlowsSearch
 * hubs); reduced motion resolves it to instant open/close. `reduced` is threaded
 * from the feed (one hook call at the top, not one per row). */
function FlowsExpansion({ open, reduced, children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateRows: open ? '1fr' : '0fr',
      transition: reduced ? 'none' : 'grid-template-rows 340ms cubic-bezier(0.16,1,0.3,1)',
    }}>
      <div style={{ overflow: 'hidden', minHeight: 0 }}>{children}</div>
    </div>
  );
}

// Stable per-row identity — shared by React keys, the expandedKey state, and
// the scroll-target ref map. Null-station rows (stations-failed fallback) key
// off the event alone.
function rowKeyOf(row) {
  return row.event.id || `${row.station ? row.station.id : 'raw'}:${row.event.title}`;
}

function FlowsEventFeed({
  events, stations, eventMap, distances, routePrev, lines, lastTrain, headways, fares,
  activeStation, homeStation, visibleSystems, stationsFailed, loading, graphReady, graphFailed,
  isMobile, reducedMotion, showSetHomeCard, expandStation, linkedEvent, closeSignal, selectedStation,
  darkMode, radius, onSelectStation, onFindMyStation, onPickMyself, onReachableCount, onRouteReady,
  onEventOpen, onEventClose,
}) {
  const FF = window.FlowsFeedFormat;
  const home = homeStation || activeStation || null;
  const lens = darkMode ? FEED_LENS.dark : FEED_LENS.light;
  const [expandedKey, setExpandedKey] = React.useState(null);
  const [desktopOpenKey, setDesktopOpenKey] = React.useState(null); // B8 (K2) — the row whose desktop detail panel is open
  const desktopOpenStationRef = React.useRef(null);                 // station id behind desktopOpenKey — lets the sync effect spot an out-of-feed selection change
  const [longHaulOpen, setLongHaulOpen] = React.useState(false);
  const [expandedBuckets, setExpandedBuckets] = React.useState(() => new Set()); // Goal 2 — bucket keys revealed past the cap (independent per bucket)
  const [pulseKey, setPulseKey] = React.useState(null); // deep-link orientation pulse (§2.2)
  const rowRefs = React.useRef(new Map()); // rowKey → wrapper el (mobile scroll target)
  const linkedScrolledRef = React.useRef(null); // deep-link oriented once per event

  // Row fade-up keyframe (injected once; matches the sidebar fade pattern).
  React.useEffect(() => {
    if (document.getElementById('flows-feed-keyframes')) return;
    const s = document.createElement('style');
    s.id = 'flows-feed-keyframes';
    s.textContent = '@keyframes flowsFeedRowIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}'
      // Deep-link orientation pulse (§2.2) — one amber wash fading to row bg;
      // non-flashing, WCAG 2.3.1-safe. Sits behind the translucent row surface.
      + '@keyframes flowsLinkedPulse{from{background:rgba(213,120,0,0.13)}to{background:transparent}}';
    document.head.appendChild(s);
  }, []);

  const stById = React.useMemo(() => {
    const m = {};
    for (const s of (stations || [])) if (s) m[s.id] = s;
    return m;
  }, [stations]);

  // Nearest-hub group for the no-home variant (§4.6).
  const hubPoints = React.useMemo(() => {
    const pts = [];
    const hubs = window.CITY_HUBS || {};
    for (const id of Object.values(hubs)) {
      const st = stById[id];
      if (st && HUB_GROUP[id]) pts.push({ lat: st.lat, lng: st.lng, group: HUB_GROUP[id] });
    }
    return pts;
  }, [stById]);
  const nearestGroup = React.useCallback((st) => {
    if (!hubPoints.length || !window.haversine) return 'SF';
    let best = hubPoints[0].group, bestD = Infinity;
    for (const p of hubPoints) {
      const d = window.haversine(st.lat, st.lng, p.lat, p.lng);
      if (d < bestD) { bestD = d; best = p.group; }
    }
    return best;
  }, [hubPoints]);

  // ── Core view model (§4.2 population + §4.4 buckets/sort) ──
  const view = React.useMemo(() => {
    if (!FF) return { mode: 'home', buckets: { under20: [], quick: [], worth: [], longhaul: [] }, reachableCount: 0, hasLongHaul: false, totalToday: 0 };
    const today = FF.pacificToday();
    const chrono = (a, b) => {
      if (a.startMin == null && b.startMin == null) return 0;
      if (a.startMin == null) return 1;
      if (b.startMin == null) return -1;
      return a.startMin - b.startMin;
    };

    // §4.5 E3, stations-failed flavor (phase-review I3): with no stations there
    // is no matching, no home, no lens — fall back to a plain chrono list of
    // today's RAW events (no minutes, no station line). The page never dies.
    if (stationsFailed && (!stations || stations.length === 0)) {
      const list = (Array.isArray(events) ? events : [])
        .filter((ev) => ev && FF.isEventToday(ev, today))
        .map((ev) => ({ event: ev, station: null, reachMin: null, startMin: ev.timeUnresolved === true ? null : FF.parseEventStartMinutes(ev.time) }))
        .sort(chrono);
      return { mode: 'degraded', list, totalToday: list.length };
    }

    // Collect today's matched events once. §4.2: "station in a visible
    // system" — filtered at collection so ALL modes (home/nohome/degraded)
    // honor the toggles, preserving feed ↔ map parity (§6.3: a toggled-off
    // system has no markers, so it must have no rows). Absent prop → all
    // systems visible (defensive; FlowsApp always passes the Set).
    const todayRows = [];
    if (eventMap && eventMap.forEach) {
      eventMap.forEach((evs, stationId) => {
        const st = stById[stationId];
        if (!st) return;
        if (visibleSystems && !visibleSystems.has(st.system)) return;
        for (const ev of evs) {
          if (!FF.isEventToday(ev, today)) continue;
          // C2: a timeUnresolved row (two published clocks, merge refused) has
          // no trustworthy start minute — null fires the EXISTING 'neutral'
          // tier, never 'after-last'. Degrade, don't assert.
          todayRows.push({ event: ev, station: st, startMin: ev.timeUnresolved === true ? null : FF.parseEventStartMinutes(ev.time) });
        }
      });
    }

    if (!home) {
      // No-home variant — group by nearest hub, chrono within (§4.6).
      const groups = {};
      for (const r of todayRows) {
        const g = nearestGroup(r.station);
        (groups[g] = groups[g] || []).push(r);
      }
      Object.keys(groups).forEach((k) => groups[k].sort(chrono));
      return { mode: 'nohome', groups, totalToday: todayRows.length };
    }

    // B11: separate a SETTLED graph failure (E3 degraded) from a graph still in
    // flight (skeleton). Pre-B11 both read as !graphReady, so the "TRAVEL TIMES
    // ARE DOWN" kicker flashed during the normal load window before edges arrived.
    if (graphFailed) {
      // Degraded — plain chrono list, no minutes (§4.5 E3).
      const list = todayRows.slice().sort(chrono);
      return { mode: 'degraded', list, totalToday: todayRows.length };
    }
    if (!graphReady) {
      // Graph in flight (home set, edges not loaded, not failed) → hold on the
      // loading skeleton, not a wrong empty/degraded state.
      return { mode: 'loading', totalToday: todayRows.length };
    }

    // Home set — bucket by reach minutes, chrono within, after-last sinks.
    const now = window.currentBand ? window.currentBand() : { band: 'late', dayType: 'weekday' };
    const routeCache = {};
    const buckets = { under20: [], quick: [], worth: [], longhaul: [] };
    let reachableCount = 0;
    for (const r of todayRows) {
      const d = distances && distances.get ? distances.get(r.station.id) : null;
      if (!d || !isFinite(d.time)) continue;
      const reachMin = d.time;
      const bucketKey = FF.bucketForMinutes(reachMin);
      if (!bucketKey) continue;
      let info = routeCache[r.station.id];
      if (!info) {
        const steps = window.buildRouteSteps ? window.buildRouteSteps(home.id, r.station.id, routePrev, stations, lines) : [];
        const rides = steps.filter((s) => s.kind === 'ride');
        info = {
          firstLineId: rides.length ? rides[0].lineId : null,
          returnLineId: rides.length ? rides[rides.length - 1].lineId : null,
          // Adult Clipper price of these steps — null until fares.json loads or
          // when a leg can't be priced (lib/flows-fares.js never estimates).
          fareCents: (fares && window.FlowsFares) ? window.FlowsFares.tripFareCents(steps, fares, stations) : null,
        };
        routeCache[r.station.id] = info;
      }
      const headway = FF.bandHeadway(info.firstLineId, headways, now.band, now.dayType);
      const tier = (info.returnLineId && lastTrain && r.startMin != null && window.lastTrainTier)
        ? window.lastTrainTier(r.startMin, info.returnLineId, now.dayType, lastTrain, { todayYmd: today.ymd })
        : null;
      const chip = tier ? FF.chipInfo(tier) : null;
      buckets[bucketKey].push({
        event: r.event, station: r.station, reachMin,
        band: window.bandForMinutes ? window.bandForMinutes(reachMin) : null,
        startMin: r.startMin, headway, tier, chip, fareCents: info.fareCents,
      });
      if (bucketKey !== 'longhaul') reachableCount++;
    }
    const sortBucket = (arr) => {
      const notLast = arr.filter((r) => !(r.chip && r.chip.colorKey === 'afterlast'));
      const last = arr.filter((r) => r.chip && r.chip.colorKey === 'afterlast');
      notLast.sort(chrono); last.sort(chrono);
      return notLast.concat(last);
    };
    Object.keys(buckets).forEach((k) => { buckets[k] = sortBucket(buckets[k]); });
    return {
      mode: 'home', buckets, reachableCount,
      hasLongHaul: buckets.longhaul.length > 0,
      totalToday: todayRows.length,
    };
  }, [FF, events, eventMap, distances, routePrev, stations, lines, lastTrain, headways, fares, home, graphReady, graphFailed, stationsFailed, stById, nearestGroup, visibleSystems]);

  // Live region (§6.5): announce the reachable count after the feed computes.
  // Deps carry the ANNOUNCEMENT'S CONTENT sources, not just the number: the
  // string names the home station, so changing home between stations with an
  // identical count must still re-fire (FlowsApp's announce() no-ops when the
  // resulting string is byte-identical, so this never double-speaks).
  const reachN = view.mode === 'home' ? view.reachableCount : -1;
  const homeId = home ? home.id : null;
  React.useEffect(() => {
    if (view.mode === 'home' && onReachableCount) onReachableCount(view.reachableCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachN, homeId, visibleSystems]);

  const rowTap = React.useCallback((row) => {
    if (isMobile) {
      if (onSelectStation && row.station) onSelectStation(row.station);
      const key = rowKeyOf(row);
      const willOpen = expandedKey !== key;
      setExpandedKey(willOpen ? key : null);
      // §6.5 "Route ready…" fires from the surface that actually opens: on
      // mobile that's this inline expansion (desktop announces from FlowsApp's
      // detail-panel effect). Open only — closing announces nothing.
      if (willOpen && row.reachMin != null && onRouteReady) onRouteReady(row.reachMin);
      // §2.5 U2/U3 — the URL mirrors the open event; a close clears it.
      if (willOpen) { if (onEventOpen) onEventOpen(row.event); }
      else if (onEventClose) onEventClose();
      return;
    }
    // Desktop (§6.4 K2): the detail panel below the map is the surface. Tapping
    // (or Enter-activating) the SAME row again toggles it closed → back to the
    // default home detail (or none in browse); focus stays on the row (it's the
    // active element). Opening a different row selects its station + mirrors U2.
    const key = rowKeyOf(row);
    if (desktopOpenKey === key) {
      if (onSelectStation) onSelectStation(home || null); // return to home detail (null → closes)
      if (onEventClose) onEventClose();
      desktopOpenStationRef.current = null;
      setDesktopOpenKey(null);
    } else {
      if (onSelectStation && row.station) onSelectStation(row.station);
      if (onEventOpen) onEventOpen(row.event);
      desktopOpenStationRef.current = row.station ? row.station.id : null;
      setDesktopOpenKey(key);
    }
  }, [isMobile, onSelectStation, expandedKey, desktopOpenKey, home, onRouteReady, onEventOpen, onEventClose]);

  // B8 (K2) — Esc-close comes from FlowsApp (document listener) via a bumped
  // closeSignal; return focus to the row that opened the panel (§6.2). The
  // second-Enter/click close above keeps focus on the row itself, so it does
  // NOT bump the signal — this effect is Esc-only.
  React.useEffect(() => {
    if (!closeSignal) return; // 0 on mount
    const key = desktopOpenKey;
    if (key) {
      const el = rowRefs.current.get(key);
      const btn = el && el.querySelector('button');
      if (btn) btn.focus();
      desktopOpenStationRef.current = null;
      setDesktopOpenKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeSignal]);

  // K2 sync — keep the desktop open-key honest about the detail target. When the
  // selection changes from OUTSIDE the feed (a desktop map-marker tap, or a home
  // change) the previously-opened row's panel is no longer showing, so its key is
  // stale: clear it. Without this, a second Enter on that row would toggle-CLOSE
  // (it still equals the stale key) instead of opening, and an Esc would refocus a
  // row whose panel isn't up. Opening a row FROM the feed sets both the key and
  // this ref to the same station, so an in-feed open never trips the divergence
  // check — only genuinely-external selection moves clear the key.
  React.useEffect(() => {
    if (!desktopOpenKey) return;
    const selId = selectedStation ? selectedStation.id : null;
    if (selId !== desktopOpenStationRef.current) {
      desktopOpenStationRef.current = null;
      setDesktopOpenKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation]);

  // Mobile map-marker tap (phase-review I2): FlowsApp forwards the tap as an
  // expand request — {stationId, n}. Find the station's SOONEST feed row
  // (buckets are chrono within; a station's rows share one bucket), auto-open
  // LONG HAUL if that's where it lives, expand it, announce, scroll it into
  // view. After C1 every map marker station has a tonight row by construction.
  React.useEffect(() => {
    if (!expandStation || !expandStation.stationId || !isMobile) return;
    if (view.mode !== 'home') return;
    const b = view.buckets;
    let target = null;
    let inLongHaul = false;
    for (const k of ['under20', 'quick', 'worth', 'longhaul']) {
      const hit = (b[k] || []).find((r) => r.station && r.station.id === expandStation.stationId);
      if (hit) { target = hit; inLongHaul = (k === 'longhaul'); break; }
    }
    if (!target) return; // defensive — C1 makes this unreachable from a marker
    if (inLongHaul) setLongHaulOpen(true);
    const key = rowKeyOf(target);
    setExpandedKey(key);
    if (target.reachMin != null && onRouteReady) onRouteReady(target.reachMin);
    if (onEventOpen) onEventOpen(target.event); // U2 — map tap routed to a row
    const timer = setTimeout(() => {
      const el = rowRefs.current.get(key);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      }
    }, 60); // after the (possibly longhaul-opening) commit paints the row
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandStation]);

  // Deep-link arrival (§2.2/§2.6) — orient to the linked event ONCE. A pinned
  // (non-tonight) event uses its synthetic row; a tonight event uses its real
  // bucket row (auto-opening THE LONG HAUL if it lives there). Mobile expands +
  // focuses the row button; desktop leaves focus to the panel h2 (FlowsApp).
  // Existing-row arrivals get the one amber orientation pulse; the pinned row is
  // fresh content and needs none. FlowsApp owns the single L1 announcement, so
  // this never calls onRouteReady (no double-speak).
  React.useEffect(() => {
    if (!linkedEvent) { linkedScrolledRef.current = null; return; }
    if (view.mode !== 'home') return;
    const ev = linkedEvent.event;
    const evKey = ev.id || ev.slug;
    if (linkedScrolledRef.current === evKey) return; // oriented already (survives U4 home change)

    const pinned = linkedEvent.pinnedRow;
    let key = null, inLongHaul = false;
    if (pinned) {
      key = rowKeyOf(pinned);
    } else {
      const b = view.buckets;
      for (const k of ['under20', 'quick', 'worth', 'longhaul']) {
        const hit = (b[k] || []).find((r) => r.event && (r.event.id === ev.id || (ev.slug && r.event.slug === ev.slug)));
        if (hit) { key = rowKeyOf(hit); inLongHaul = (k === 'longhaul'); break; }
      }
    }
    if (!key) return; // row not in the view yet — retry when it recomputes

    linkedScrolledRef.current = evKey; // commit: no re-scroll/re-focus on later view changes
    if (inLongHaul) setLongHaulOpen(true);
    if (isMobile) setExpandedKey(key);
    const timer = setTimeout(() => {
      const el = rowRefs.current.get(key);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      if (isMobile && el) { const btn = el.querySelector('button'); if (btn) btn.focus(); } // §2.6 mobile focus
      if (!pinned && !reducedMotion) {                    // §2.2 orientation pulse — existing-row only
        setPulseKey(key);
        setTimeout(() => setPulseKey((k) => (k === key ? null : k)), 1300);
      }
    }, 80); // after the (possibly longhaul-opening) commit paints the row
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedEvent, view, isMobile, reducedMotion]);

  // ── Shared style fragments ──
  const titleWrap = { padding: '4px 2px 0' };
  const h2Style = {
    fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 900,
    letterSpacing: '-0.01em', textTransform: 'uppercase', color: lens.ink, margin: 0, lineHeight: 1.1,
  };
  const kickerStyle = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: lens.muted, marginTop: 4,
  };
  const sectionHeader = {
    fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 700,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: lens.muted,
    marginTop: 24, marginBottom: 10,
  };
  const countStyle = {
    fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 600,
    // Goal 6 audit: the count is information (the FULL bucket size behind the
    // cap), so it must clear AA — the old 0.55/0.6-alpha fade measured 2.5:1.
    // Solid lens.muted measures 5.6:1 in both themes; hierarchy vs the header
    // stays via weight (600 body vs 700 display).
    color: lens.muted, marginLeft: 6,
  };
  // One dashed-ghost reveal control, shared by THE LONG HAUL collapse and the
  // per-bucket SHOW MORE cap (Goal 2) so both speak the same language. Callers
  // set marginTop (LONG HAUL floats below the feed; SHOW MORE tucks under its bucket).
  const ghostRevealStyle = {
    display: 'block', width: '100%', minHeight: 44, cursor: 'pointer',
    fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 11,
    letterSpacing: '0.1em', textTransform: 'uppercase', color: lens.muted,
    background: 'transparent', border: `1px dashed ${lens.hairline}`,
    borderRadius: Math.min(radius, 12), padding: '12px 14px',
  };

  const displayName = (st) => (window.displayStationName ? window.displayStationName(st) : (st ? st.name : ''));

  const renderRow = (row, i) => {
    const expandKey = rowKeyOf(row);
    // Compose the entrance fade-up with the deep-link orientation pulse (they
    // animate different properties, so they coexist as a comma list).
    const rowIn = reducedMotion ? '' : `flowsFeedRowIn 300ms ease ${Math.min(i, 8) * 30}ms both`;
    const pulse = expandKey === pulseKey ? 'flowsLinkedPulse 1200ms ease-out' : '';
    const anim = [rowIn, pulse].filter(Boolean).join(', ') || 'none';
    return (
      // Goal 6 audit: rows live inside a <ul>, so the wrapper is a real <li> —
      // the div-in-ul form gave AT a list with zero list items.
      <li
        key={expandKey}
        ref={(el) => { if (el) rowRefs.current.set(expandKey, el); else rowRefs.current.delete(expandKey); }}
        style={{ animation: anim, borderRadius: Math.min(radius, 12), listStyle: 'none' }}>
        <FlowsFeedRow
          row={row}
          isMobile={isMobile}
          isExpanded={expandedKey === expandKey}
          onTap={rowTap}
          homeStation={home}
          routePrev={routePrev}
          stations={stations}
          lines={lines}
          lastTrain={lastTrain}
          reducedMotion={reducedMotion}
          darkMode={darkMode}
          radius={radius}
        />
      </li>
    );
  };

  // Goal 6 audit — reveal-focus continuity: the SHOW MORE / LONG HAUL buttons
  // unmount on activation, which dropped keyboard focus to <body> (the user
  // lost their place). Hand focus to the first newly revealed row instead.
  const focusRowAfterReveal = (row) => {
    if (!row) return;
    const key = rowKeyOf(row);
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(key);
      const btn = el && el.querySelector('button');
      if (btn) btn.focus();
    });
  };

  // Goal 2 — cap a section to the soonest FEED_BUCKET_CAP rows, tucking the
  // overflow behind SHOW MORE (n). The header count stays the FULL size.
  // Hoisted above the early returns (2026-07-21 map-reachability fix) so it
  // serves BOTH the home-lens buckets and the no-home city groups with one
  // mechanism (ghost reveal + focus continuity). Keyspaces never collide:
  // buckets use under20/quick/worth/longhaul, groups use the GROUP_ORDER
  // city names. THE LONG HAUL is exempt: it owns its own collapse and shows
  // every row once opened (never double-wrap it).
  const bucketSection = (key, label, items) => {
    if (!items || items.length === 0) return null;
    const capped = key !== 'longhaul' && !expandedBuckets.has(key) && items.length > FEED_BUCKET_CAP;
    const shown = capped ? items.slice(0, FEED_BUCKET_CAP) : items;
    return (
      <section key={key} aria-label={label}>
        <div style={sectionHeader}>{label}<span style={countStyle}>({items.length})</span></div>
        <ul style={{ margin: 0, padding: 0 }}>{shown.map((row, i) => renderRow(row, i))}</ul>
        {capped && (
          <button
            className="focus-double-ring"
            onClick={() => {
              setExpandedBuckets((prev) => new Set(prev).add(key));
              focusRowAfterReveal(items[FEED_BUCKET_CAP]); // first newly revealed row
            }}
            style={{ ...ghostRevealStyle, marginTop: 8 }}
          >{`SHOW MORE (${items.length - FEED_BUCKET_CAP})`}</button>
        )}
      </section>
    );
  };

  // ── Loading (§4.5) — 3 skeleton rows, 56px, zero layout shift ──
  // B11: 'loading' mode = events loading OR (home set + graph still in flight).
  if (loading || view.mode === 'loading') {
    return (
      <div style={{ padding: '4px 0' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            minHeight: 56, marginBottom: 8, borderRadius: Math.min(radius, 12),
            border: `1px solid ${lens.hairline}`,
            background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(26,23,20,0.04)',
            animation: reducedMotion ? 'none' : 'skeletonPulse 1.6s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  // ── No-home variant (§4.6) ──
  if (view.mode === 'nohome') {
    return (
      <div style={{ padding: '4px 0' }}>
        <div style={titleWrap}><h2 style={h2Style}>{FEED_COPY.F2_TITLE}</h2></div>

        {/* Inline set-home card — shown in the browse state (the canonical
            "fully browsable" no-home path, AC-4.4); in unset/denied the prompt
            card already owns the set-home job in the slot above, so we don't
            double it. */}
        {showSetHomeCard && (
        <div style={{
          ...glassStyle(darkMode, 'solid'),
          border: `1px solid ${lens.hairline}`, borderRadius: Math.min(radius, 14),
          padding: 14, margin: '12px 0 8px',
        }}>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
            color: lens.muted, margin: '0 0 10px', lineHeight: 1.5,
          }}>{FEED_COPY.SETHOME_LINE}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button className="focus-double-ring" onClick={() => onFindMyStation && onFindMyStation()} style={{
              minHeight: 44, padding: '10px 16px', cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: darkMode ? '#10110F' : '#fff', background: lens.reach1,
              border: '1px solid transparent', borderRadius: Math.min(radius, 10),
            }}>{FEED_COPY.FIND}</button>
            <button className="focus-double-ring" onClick={() => onPickMyself && onPickMyself()} style={{
              minHeight: 44, padding: '10px 16px', cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 12,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              color: lens.ink, background: 'transparent',
              border: `1px solid ${lens.ink}`, borderRadius: Math.min(radius, 10),
            }}>{FEED_COPY.PICK}</button>
          </div>
        </div>
        )}

        {/* City groups cap at FEED_BUCKET_CAP + SHOW MORE (n) — the SAME
            bucketSection mechanism as the home lens (deliberately also on
            desktop's no-home column — consistency win, Zora-flagged +
            Mav-blessed). The reveal is a free, local, one-tap client-side
            expand — NEVER gated on setting a home station (Mav's hard
            condition, pinned in tests/flows-mobile-layout.test.js). The
            group header keeps the TRUE total count. */}
        {GROUP_ORDER.filter((g) => view.groups[g] && view.groups[g].length).map((g) =>
          bucketSection(g, g, view.groups[g])
        )}
        {view.totalToday === 0 && (
          <p style={{ ...sectionHeader, marginTop: 20, color: lens.muted }}>{FEED_COPY.E2}</p>
        )}
      </div>
    );
  }

  // ── Degraded — travel times down, plain chrono list (§4.5 E3) ──
  if (view.mode === 'degraded') {
    return (
      <div style={{ padding: '4px 0' }}>
        <div style={titleWrap}>
          <h2 style={h2Style}>{FEED_COPY.F1_TITLE}</h2>
          <div style={{ ...kickerStyle, color: lens.reach1 }}>{FEED_COPY.E3}</div>
        </div>
        <ul style={{ margin: '12px 0 0', padding: 0 }}>
          {view.list.map((r, i) => renderRow({ event: r.event, station: r.station, reachMin: null, startMin: r.startMin }, i))}
        </ul>
      </div>
    );
  }

  // ── Home set — the lens ──
  const b = view.buckets;
  const anyReachable = b.under20.length || b.quick.length || b.worth.length;

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={titleWrap}>
        <h2 style={h2Style}>{FEED_COPY.F1_TITLE}</h2>
        <div style={kickerStyle}>{`FROM ${displayName(home).toUpperCase()}`}</div>
      </div>

      {/* JUMP TO MAP (mobile home-set, 2026-07-21) — feed-first stays the
          payoff, so the map gets a static, non-sticky in-page jump instead of
          a scroll past every row. Visually subordinate by design (Mav): dashed
          ghost text link, no card/background/icon — THE ONE YOU CAME FOR below
          must still read as the first real content block. Behavior per Iris's
          ruling (Option B): scroll first (house reduced-motion aware), THEN
          focus the tabindex=-1 region with preventScroll so the smooth scroll
          isn't yanked. Renders in home-set mobile only. */}
      {isMobile && (
        <a
          href="#flows-map-region"
          className="focus-double-ring"
          onClick={(e) => {
            e.preventDefault();
            const region = document.getElementById('flows-map-region');
            if (!region) return;
            if (region.scrollIntoView) region.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
            region.focus({ preventScroll: true });
          }}
          style={{
            ...ghostRevealStyle,
            color: lens.reach1,             // AA both themes (E3-kicker precedent ink)
            boxSizing: 'border-box',        // <a> defaults content-box; 100% width + padding must not overflow
            textAlign: 'center', textDecoration: 'none',
            marginTop: 12,
          }}
        >{FEED_COPY.JUMP}</a>
      )}

      {/* THE ONE YOU CAME FOR (§2.2) — pinned deep-link row for a non-tonight
          event (tonight links orient to their real bucket row instead). First
          in the feed DOM = first visually (§2.6 DOM order = visual order). The
          pinned row is NEVER in eventMap, so the map gains no marker (§6.3). */}
      {linkedEvent && linkedEvent.pinnedRow && (
        <section aria-label={FEED_COPY.F10}>
          <div style={{ ...sectionHeader, marginTop: 16 }}>{FEED_COPY.F10}</div>
          <ul style={{ margin: 0, padding: 0 }}>{renderRow(linkedEvent.pinnedRow, 0)}</ul>
        </section>
      )}

      {/* Empty states (§4.5): nothing ≤60 but long-haul exists → E1 (+ the
          expander below); nothing at all → E2. */}
      {!anyReachable && (view.hasLongHaul ? (
        <div style={{ padding: '24px 4px' }}>
          <p style={{ ...h2Style, fontSize: 15, marginBottom: 6 }}>
            {`${FEED_COPY.E1_A}${displayName(home).toUpperCase()}.`}
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: lens.muted, margin: 0, lineHeight: 1.5 }}>
            {FEED_COPY.E1_B}
          </p>
        </div>
      ) : (
        <div style={{ padding: '24px 4px' }}>
          <p style={{ ...h2Style, fontSize: 15, margin: 0, lineHeight: 1.4 }}>{FEED_COPY.E2}</p>
        </div>
      ))}

      {FF.BUCKET_ORDER.filter((k) => k !== 'longhaul').map((k) =>
        bucketSection(k, FF.BUCKET_LABEL[k], b[k])
      )}

      {/* THE LONG HAUL — collapsed behind a ghost button (§4.4) */}
      {view.hasLongHaul && (
        longHaulOpen
          ? bucketSection('longhaul', FF.BUCKET_LABEL.longhaul, b.longhaul)
          : (
            <button
              className="focus-double-ring"
              onClick={() => {
                setLongHaulOpen(true);
                focusRowAfterReveal(b.longhaul[0]); // same continuity as SHOW MORE
              }}
              style={{ ...ghostRevealStyle, marginTop: 20 }}
            >{`SHOW THE LONG HAUL (${b.longhaul.length})`}</button>
          )
      )}

      {/* Estimates footnote — F6 (§4.4) */}
      {(anyReachable || view.hasLongHaul) && (
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 400,
          color: lens.muted, margin: '18px 2px 0', lineHeight: 1.5,
        }}>
          <span aria-hidden="true" style={{ marginRight: 6, color: lens.muted }}>&mdash;</span>
          {FEED_COPY.F6}
        </p>
      )}
    </div>
  );
}

FlowsFeedRow.propTypes = {
  row: PropTypes.object.isRequired,
  isMobile: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onTap: PropTypes.func.isRequired,
  homeStation: PropTypes.object,
  routePrev: PropTypes.object,
  stations: PropTypes.array,
  lines: PropTypes.object,
  lastTrain: PropTypes.object,
  reducedMotion: PropTypes.bool,
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
};

FlowsEventFeed.propTypes = {
  events: PropTypes.array, // raw events — the stations-failed E3 fallback source
  stations: PropTypes.array,
  eventMap: PropTypes.object,
  distances: PropTypes.object,
  routePrev: PropTypes.object,
  lines: PropTypes.object,
  lastTrain: PropTypes.object,
  headways: PropTypes.object,
  fares: PropTypes.object, // data/fares.json (adult Clipper) — null until loaded; home-mode rows carry fareCents
  activeStation: PropTypes.object,
  homeStation: PropTypes.object,
  visibleSystems: PropTypes.object, // Set — same convention as FlowsTransitMap
  stationsFailed: PropTypes.bool,
  loading: PropTypes.bool,
  graphReady: PropTypes.bool,
  graphFailed: PropTypes.bool, // B11 — settled graph failure gates E3 (vs in-flight skeleton)
  isMobile: PropTypes.bool,
  reducedMotion: PropTypes.bool,
  showSetHomeCard: PropTypes.bool,
  expandStation: PropTypes.object, // {stationId, n} — mobile map-tap expand request
  linkedEvent: PropTypes.object,   // {event, station, pinnedRow} — deep-link arrival (§2.2)
  closeSignal: PropTypes.number,   // B8 (K2) — bumped when Esc closes the desktop panel → refocus the row
  selectedStation: PropTypes.object, // K2 — desktop detail target; feed clears a stale open-key when this diverges (out-of-feed selection)
  darkMode: PropTypes.bool.isRequired,
  radius: PropTypes.number.isRequired,
  onSelectStation: PropTypes.func,
  onFindMyStation: PropTypes.func,
  onPickMyself: PropTypes.func,
  onReachableCount: PropTypes.func,
  onRouteReady: PropTypes.func, // (minutes) — mobile expansion opened (§6.5)
  onEventOpen: PropTypes.func,   // (event) — a row opened → U2 URL mirror
  onEventClose: PropTypes.func,  // () — mobile expansion closed → U3 URL clear
};

window.FlowsExpansion = FlowsExpansion;
window.FlowsFeedRow = FlowsFeedRow;
window.FlowsEventFeed = FlowsEventFeed;
